import { assert } from '@ember/debug';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import dayjs from 'dayjs';
import type { FormData, FormKey } from 'ember-headless-form';
import { modifier } from 'ember-modifier';
import InputControl, {
  type InputControlSignature
} from '../../../components/form/controls/input.gts';
import blurOnWheel from '../../modifiers/blur-on-wheel.ts';
import { type HeadlessFormFieldBlock, type UserData } from '../../types.ts';
import Confirmation from './confirmation.gts';
import Errors from './errors.gts';

export interface WrFormAnnotedInputComponentSignature<
  DATA extends UserData,
  KEY extends FormKey<FormData<DATA>> = FormKey<FormData<DATA>>
> {
  Element: InputControlSignature['Element'];
  Args: {
    name: KEY;
    confirmation?: string;
    blurOnWheel?: boolean;
    field: HeadlessFormFieldBlock<DATA, KEY>;
  };
}

export default class WrFormAnnotedInputComponent<
  DATA extends UserData,
  KEY extends FormKey<FormData<DATA>> = FormKey<FormData<DATA>>
> extends Component<WrFormAnnotedInputComponentSignature<DATA, KEY>> {
  @tracked control?: HTMLInputElement;

  registerControl = modifier((el: HTMLInputElement) => {
    this.control = el;
  });

  get isConfirmed() {
    return Boolean(this.args?.confirmation);
  }

  formatValue = (value: unknown) => {
    if (typeof value == 'number') return this.formatNumber(value);
    if (value instanceof Date) return value ? this.formatDate(value) : null;

    assert(
      `Only string, null, or undefined values are expected for ${
        this.args.name
      }, but you passed ${typeof value}`,
      typeof value === 'undefined' ||
        typeof value === 'string' ||
        value === null
    );

    return value;
  };

  formatDate = (value: Date) => {
    if (this.control?.type == 'date') return dayjs(value).format('YYYY-MM-DD');
    if (this.control?.type == 'datetime-local')
      return dayjs(value).format('YYYY-MM-DDTHH:mm');
  };

  formatNumber = (value: number) => value.toString();

  setValue = (fieldSetValue: (value: DATA[KEY]) => void) => {
    return (value: string) => {
      // in the case where the user inputs '-' in a number field, value is ''
      // if it goes through the conversion to a number, the field is set to 0, removing the -
      // so the conversion is avoided when value is ''
      // due to this mechanic, a field with - in it is saved as an empty field
      if (this.control?.type.match(/^date/)) {
        fieldSetValue(
          value ? (new Date(value) as DATA[KEY]) : (null as DATA[KEY])
        );
      } else if (this.control?.type == 'number' && value)
        fieldSetValue(+`${value}` as DATA[KEY]);
      else fieldSetValue(value as DATA[KEY]);
    };
  };

  <template>
    <InputControl
      id={{@field.id}}
      aria-invalid={{@field.isInvalid}}
      aria-describedby={{@field.errorId}}
      name={{@name}}
      @value={{this.formatValue @field.value}}
      @onChange={{this.setValue @field.setValue}}
      @isConfirmed={{this.isConfirmed}}
      @hasError={{@field.isInvalid}}
      {{this.registerControl}}
      {{blurOnWheel @blurOnWheel}}
      ...attributes
    />

    {{#if @confirmation}}
      <Confirmation @confirmation={{@confirmation}} />
    {{/if}}

    {{#if @field.isInvalid}}
      <Errors @field={{@field}} />
    {{/if}}
  </template>
}
